// Shared chart palette + helpers. Stripe-dashboard-flavored: one primary blue,
// green for positive/won, red/amber for negative/risk. Not colorful for its own sake.

export const CHART_COLORS = {
  primary: "#2563eb",       // blue-600 — main series & current period
  primaryTint: "#93c5fd",   // blue-300 — past periods
  positive: "#10b981",      // emerald-500 — won/growth
  negative: "#dc2626",      // red-600 — lost/risk
  warning: "#f59e0b",       // amber-500 — caution/mid range
  grid: "#e2e8f0",          // slate-200 — subtle gridlines
  axis: "#64748b",          // slate-500 — axis labels
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-01" -> "Jan 26" (unambiguous, tight). */
export function fmtMonthLabel(key) {
  if (!key || typeof key !== "string") return "";
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return `${MONTHS_SHORT[m - 1]} ${String(y).slice(-2)}`;
}

/** Return `series` starting at the first entry with data, keeping >= minKeep tail. */
export function trimLeadingEmpty(series, valueKey, minKeep = 3) {
  if (!Array.isArray(series) || series.length === 0) return series;
  const firstDataIdx = series.findIndex(
    (p) => p[valueKey] != null && p[valueKey] !== 0 && p[valueKey] !== "0"
  );
  if (firstDataIdx <= 0) return series; // all zero, or already starts with data
  const start = Math.min(firstDataIdx, series.length - minKeep);
  return series.slice(Math.max(0, start));
}

/** Return the "YYYY-MM" for a Date's UTC year/month. */
export function ymKey(dt) {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Current quarter's YYYY-MM keys (UTC). */
export function quarterMonths(dt = new Date(), delta = 0) {
  const y = dt.getUTCFullYear();
  const qIdx = Math.floor(dt.getUTCMonth() / 3) + delta;
  const startYear = y + Math.floor(qIdx / 4);
  const startQ = ((qIdx % 4) + 4) % 4;
  const startMonth = startQ * 3;
  return [0, 1, 2].map((i) => {
    const m = startMonth + i;
    return `${startYear}-${String(m + 1).padStart(2, "0")}`;
  });
}

export const RANGE_OPTIONS = [
  { id: "last_3m",      label: "Last 3 months" },
  { id: "last_6m",      label: "Last 6 months" },
  { id: "last_12m",     label: "Last 12 months" },
  { id: "this_quarter", label: "This quarter" },
  { id: "last_quarter", label: "Last quarter" },
  { id: "custom",       label: "Custom" },
];

const MONTHS_LONG_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-03-15" -> "15 Mar 2026" (or "15 Mar" when the two dates share a year). */
export function fmtDateShort(iso, includeYear = true) {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dd = String(d);
  const mm = MONTHS_LONG_SHORT[m - 1];
  return includeYear ? `${dd} ${mm} ${y}` : `${dd} ${mm}`;
}

export function formatCustomLabel(fromIso, toIso) {
  if (!fromIso || !toIso) return "";
  const [fy] = fromIso.split("-");
  const [ty] = toIso.split("-");
  const sameYear = fy === ty;
  return `${fmtDateShort(fromIso, !sameYear)} – ${fmtDateShort(toIso)}`;
}

/** Validate a custom range. Returns error string or null. */
export function validateCustomRange(from, to) {
  if (!from || !to) return "Pick both dates";
  if (from > to) return "From-date must be on or before to-date";
  // Max span 24 months (approximation: 24 * 31 days = 744 days, but we check months precisely)
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  const spanMonths = (ty - fy) * 12 + (tm - fm);
  if (spanMonths > 24) return "Range cannot exceed 24 months";
  return null;
}

export function loadCustomRange(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.from && parsed.to) return parsed;
    }
  } catch (_e) { /* localStorage disabled */ }
  // Sensible default: last 6 months up to today
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const past = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const from = past.toISOString().slice(0, 10);
  return { from, to };
}

export function saveCustomRange(storageKey, range) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(range));
  } catch (_e) { /* localStorage disabled */ }
}

/** Filter a monthly series (12 entries oldest->newest) by range id, or by
 * custom range when id === "custom". Monthly aggregation stays monthly —
 * we simply drop months whose YYYY-MM key falls outside the window.
 */
export function filterByRange(series, rangeId, customRange = null) {
  if (!Array.isArray(series)) return series;
  if (rangeId === "last_3m") return series.slice(-3);
  if (rangeId === "last_6m") return series.slice(-6);
  if (rangeId === "this_quarter") {
    const keys = new Set(quarterMonths(new Date(), 0));
    return series.filter((p) => keys.has(p.month));
  }
  if (rangeId === "last_quarter") {
    const keys = new Set(quarterMonths(new Date(), -1));
    return series.filter((p) => keys.has(p.month));
  }
  if (rangeId === "custom" && customRange && customRange.from && customRange.to) {
    const fromM = customRange.from.slice(0, 7); // "YYYY-MM"
    const toM = customRange.to.slice(0, 7);
    return series.filter((p) => p.month >= fromM && p.month <= toM);
  }
  // default: last_12m — return whole series
  return series;
}

/** Persist chart range per user/tab in localStorage. */
export function loadRange(storageKey, defaultId = "last_12m") {
  try {
    const v = localStorage.getItem(storageKey);
    if (v && RANGE_OPTIONS.find((o) => o.id === v)) return v;
  } catch (_e) { /* localStorage disabled */ }
  return defaultId;
}

export function saveRange(storageKey, id) {
  try {
    localStorage.setItem(storageKey, id);
  } catch (_e) { /* localStorage disabled */ }
}
